import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Avatar, Button, Card, Input, Spinner } from "../../design-system";
import { Icon, SupHeader, SupSelect, SupEmpty, CategoryIcon, StatusBadge, PriorityBadge } from "./atoms";
import { STATUS, STATUS_ORDER, PRIO, PRIO_ORDER } from "./data";
import { useSoporteNav } from "./nav";
import { useSupportMeta } from "./useMeta";
import { getSupportTickets } from "../../helpers/kronaly_helper";

const unwrap = (r) => r?.data ?? r;

// Presets soportados por el backend (ListTicketsQuery / controller).
const PRESETS = [
  { id: "todos", label: "Todos" },
  { id: "abiertos", label: "Abiertos" },
  { id: "sin_asignar", label: "Sin asignar" },
  { id: "mios", label: "Míos" },
];

/**
 * Soporte IT · Tickets — listado filtrable contra GET /support/tickets.
 */
function SoporteTickets() {
  document.title = "Tickets · Soporte IT | Kronaly";
  const nav = useSoporteNav();
  const [searchParams] = useSearchParams();
  const initialPreset = searchParams.get("preset");

  const { categoriesById, roots, techniciansById } = useSupportMeta();

  const [preset, setPreset] = useState(
    initialPreset && PRESETS.some((p) => p.id === initialPreset) ? initialPreset : "abiertos"
  );
  const [q, setQ] = useState("");
  const [fEstado, setFEstado] = useState("");
  const [fPrioridad, setFPrioridad] = useState("");
  const [fCategoria, setFCategoria] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carga (con debounce para la búsqueda de texto).
  useEffect(() => {
    let alive = true;
    const params = {};
    if (preset && preset !== "todos") params.preset = preset;
    if (fEstado) params.status = fEstado;
    if (fPrioridad) params.priority = fPrioridad;
    if (fCategoria) params.category_id = fCategoria;
    if (q.trim()) params.q = q.trim();

    const t = setTimeout(() => {
      setLoading(true);
      getSupportTickets(params)
        .then((r) => {
          if (!alive) return;
          setRows(unwrap(r) || []);
          setError(null);
        })
        .catch((e) => {
          if (alive) setError(e?.message || String(e));
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    }, 300);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [preset, q, fEstado, fPrioridad, fCategoria]);

  const activeFilters = [fEstado, fPrioridad, fCategoria].filter(Boolean).length;

  return (
    <div className="kr-page">
      <SupHeader
        eyebrow="Soporte IT"
        title="Tickets"
        subtitle="Incidencias de software, servicios, accesos y, cuando aplica, equipos."
        actions={
          <Button variant="primary" icon="Plus" onClick={() => nav.go("crear")}>
            Nuevo ticket
          </Button>
        }
      />

      {/* preset chips */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPreset(p.id)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7, height: 32, padding: "0 13px",
              border: `1px solid ${preset === p.id ? "var(--kr-fg-strong)" : "var(--kr-border)"}`,
              background: preset === p.id ? "var(--kr-fg-strong)" : "#fff",
              color: preset === p.id ? "#fff" : "var(--kr-fg-primary)",
              borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <Card padded={false}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--kr-border-subtle)" }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por número, título o descripción…" icon="Search" style={{ flex: 1, maxWidth: 420 }} />
          <div style={{ flex: 1 }} />
          <Button variant={showFilters || activeFilters ? "soft" : "ghost"} size="sm" icon="Filter" onClick={() => setShowFilters((s) => !s)}>
            Filtros{activeFilters ? ` · ${activeFilters}` : ""}
          </Button>
        </div>

        {showFilters && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, padding: "14px 16px", borderBottom: "1px solid var(--kr-border-subtle)", background: "var(--kr-neutral-25)" }} data-responsive-grid="3">
            <SupSelect value={fEstado} onChange={setFEstado} placeholder="Estado" options={STATUS_ORDER.map((s) => ({ value: s, label: STATUS[s].label }))} />
            <SupSelect value={fPrioridad} onChange={setFPrioridad} placeholder="Prioridad" options={PRIO_ORDER.map((p) => ({ value: p, label: PRIO[p].label }))} />
            <SupSelect value={fCategoria} onChange={setFCategoria} placeholder="Categoría" options={roots.map((c) => ({ value: c.id, label: c.name }))} />
          </div>
        )}

        <div data-scroll-x style={{ overflowX: "auto" }}>
          {error ? (
            <div style={{ padding: 16 }}><Alert tone="danger" title="No se pudieron cargar los tickets">{error}</Alert></div>
          ) : loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}><Spinner size={20} /></div>
          ) : (
            <TicketTable rows={rows} categoriesById={categoriesById} techniciansById={techniciansById} onOpen={(id) => nav.go("ticket", { code: id })} />
          )}
        </div>

        {!loading && !error && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: "1px solid var(--kr-border-subtle)", color: "var(--kr-fg-muted)", fontSize: 12 }}>
            <span><span style={{ color: "var(--kr-fg-strong)", fontWeight: 600 }}>{rows.length}</span> tickets</span>
          </div>
        )}
      </Card>
    </div>
  );
}

function TicketTable({ rows, categoriesById, techniciansById, onOpen }) {
  const cols = "96px 2.3fr 1.3fr 1.2fr 0.9fr 1fr 1.1fr";
  if (!rows.length) return <SupEmpty icon="search" title="Sin resultados" hint="Prueba a cambiar los filtros o el término de búsqueda." />;
  return (
    <div style={{ minWidth: 900 }}>
      <div style={{
        display: "grid", gridTemplateColumns: cols, gap: 12, padding: "10px 20px",
        background: "var(--kr-neutral-50)", borderBottom: "1px solid var(--kr-border)",
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600, color: "var(--kr-fg-muted)",
      }}>
        <span>Código</span><span>Ticket</span><span>Usuario</span><span>Categoría</span><span>Prioridad</span><span>Estado</span><span>Responsable</span>
      </div>
      {rows.map((t, i) => (
        <TicketRow key={t.id} t={t} cols={cols} categoriesById={categoriesById} techniciansById={techniciansById} onOpen={() => onOpen(t.id)} last={i === rows.length - 1} />
      ))}
    </div>
  );
}

function TicketRow({ t, cols, categoriesById, techniciansById, onOpen, last }) {
  const [hover, setHover] = useState(false);
  const cat = t.category_id ? categoriesById[t.category_id] : null;
  const sub = t.subcategory_id ? categoriesById[t.subcategory_id] : null;
  // El nombre del responsable viene resuelto del backend (vale aunque ya no
  // sea técnico); fallback al mapa de técnicos por compatibilidad.
  const tech = t.assignee || (t.assignee_membership_id ? techniciansById[t.assignee_membership_id] : null);
  const requesterName = t.requester?.name || "—";
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onClick={onOpen}
      style={{
        display: "grid", gridTemplateColumns: cols, gap: 12, alignItems: "center", padding: "12px 20px",
        borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
        background: hover ? "var(--kr-bg-subtle)" : "#fff", cursor: "pointer", fontSize: 13,
      }}>
      <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 11, color: "var(--kr-fg-muted)", fontWeight: 600 }}>{(t.number || "").replace(/^SOP-\d{4}-/, "#")}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <CategoryIcon icon={cat?.icon} color={cat?.color} size={30} />
        <div style={{ minWidth: 0 }}>
          <div style={{ color: "var(--kr-fg-strong)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</div>
          <div style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>{t.service_label || sub?.name || cat?.name || "—"}</div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <Avatar name={requesterName} size={26} />
        <span style={{ color: "var(--kr-fg-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{requesterName}</span>
      </div>
      <div style={{ minWidth: 0, fontSize: 12, color: "var(--kr-fg-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {cat?.name || <span style={{ color: "var(--kr-fg-subtle)" }}>—</span>}
      </div>
      <div><PriorityBadge priority={t.priority} /></div>
      <div><StatusBadge status={t.status} /></div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        {tech ? (
          <>
            <Avatar name={tech.name} size={24} />
            <span style={{ color: "var(--kr-fg-strong)", fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tech.name}</span>
          </>
        ) : (
          <span style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>Sin asignar</span>
        )}
      </div>
    </div>
  );
}

export default SoporteTickets;
